import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Lock, LockOpen, ShieldAlert } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useLockState } from './use-lock-state';
import { useLockLifecycle } from './use-lock-lifecycle';

// Lock indicator per docs/ui/operator-ui-tds.md §5.2 — the keystone deliverable.
//
// Two modes:
//   • Interactive (default) — three states: Available (click to acquire), Held by
//     me (click to release), Held by another (read-only banner). Used on surfaces
//     that opt into manual lock management.
//   • read-only (`readOnly`) — the auto-lock-on-write-intent surfaces (rebooking
//     group cards, allocation / passenger rows). The operator NEVER clicks "lock";
//     the write itself takes the lock. So this mode renders ONLY a live badge:
//     nothing when available (no idle clutter), a subtle "Editing" pill when this
//     operator holds it, and a prominent "Being edited by X" badge when ANOTHER
//     operator holds it — the live held-by-X signal, fed by the Socket.IO fan-out.

const COUNTDOWN_TICK_MS = 1_000;

function secondsUntil(iso: string | null): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.max(0, Math.ceil(ms / 1000));
}

interface LockIndicatorProps {
  entityUrn: string;
  className?: string;
  /** Live held-by-X badge only — no manual acquire/release buttons (auto-lock model). */
  readOnly?: boolean;
}

export function LockIndicator({ entityUrn, className, readOnly = false }: LockIndicatorProps) {
  const { t } = useTranslation('admin');
  const { state, isMine, isHeld, isAvailable, isLoading } = useLockState(entityUrn);
  const { acquire, release, isAcquiring, isReleasing, conflict } = useLockLifecycle(entityUrn);
  const [secondsRemaining, setSecondsRemaining] = useState(() => secondsUntil(state?.expiresAt ?? null));

  useEffect(() => {
    setSecondsRemaining(secondsUntil(state?.expiresAt ?? null));
    if (!state?.expiresAt) return;
    const tick = window.setInterval(() => {
      setSecondsRemaining(secondsUntil(state.expiresAt));
    }, COUNTDOWN_TICK_MS);
    return () => window.clearInterval(tick);
  }, [state?.expiresAt]);

  // Room subscription is the route's concern (case-detail joins the case room
  // once), not the indicator's — a per-group indicator must NOT join a room per
  // sub-case URN. StateChanged for any sub-case in the case arrives via the
  // single case room.

  if (isLoading && !state) {
    if (readOnly) return null; // no skeleton clutter on auto-lock surfaces
    return (
      <div className={cn('inline-flex h-7 w-32 animate-pulse rounded-full bg-muted', className)} />
    );
  }

  // Read-only (auto-lock) surfaces: live held-by-X badge only, no buttons.
  if (readOnly) {
    if (isAvailable) return null; // available → nothing (the write will take the lock)
    if (isMine) {
      return (
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full border border-emerald-700/30 bg-emerald-100 px-2 py-0.5 text-[11px] font-medium',
            'text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100',
            className,
          )}
          role="status"
          data-state="held-by-me"
        >
          <Lock className="h-3 w-3" aria-hidden />
          <span>{t('lock.editing')}</span>
        </span>
      );
    }
    const heldByName =
      (state?.holder ?? conflict?.holder)?.displayName ?? t('common:holds.lock.anotherOperator');
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-full border border-amber-700/30 bg-amber-100 px-2 py-0.5 text-[11px] font-medium',
          'text-amber-900 dark:bg-amber-900/40 dark:text-amber-100',
          className,
        )}
        role="status"
        aria-live="polite"
        data-state="held-by-other"
      >
        <ShieldAlert className="h-3 w-3" aria-hidden />
        <span>
          {t('lock.beingEditedBy')} {heldByName}
          {secondsRemaining > 0 ? ` · ${secondsRemaining}s` : ''}
        </span>
      </span>
    );
  }

  if (isAvailable) {
    return (
      <button
        type="button"
        onClick={() => void acquire()}
        disabled={isAcquiring}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border bg-muted px-3 py-1 text-xs font-medium',
          'text-muted-foreground transition-colors hover:bg-muted/70',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:opacity-50',
          className,
        )}
        aria-label={t('lock.availableAria')}
        data-state="available"
      >
        <LockOpen className="h-3 w-3" aria-hidden />
        <span>{isAcquiring ? t('lock.acquiring') : t('lock.available')}</span>
      </button>
    );
  }

  if (isMine) {
    return (
      <button
        type="button"
        onClick={() => void release()}
        disabled={isReleasing}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-full border border-emerald-700/30 bg-emerald-100 px-3 py-1 text-xs font-medium',
          'text-emerald-900 transition-colors hover:bg-emerald-200',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600',
          'disabled:opacity-50',
          'dark:bg-emerald-900/40 dark:text-emerald-100 dark:hover:bg-emerald-900/60',
          className,
        )}
        aria-label={t('lock.heldByYouAria')}
        data-state="held-by-me"
      >
        <Lock className="h-3 w-3" aria-hidden />
        <span>
          {isReleasing
            ? t('lock.releasing')
            : t('lock.editingCountdown', { seconds: secondsRemaining })}
        </span>
      </button>
    );
  }

  // Held by another operator. Conflict (from a 409 the operator just hit)
  // surfaces with the same holder shape; merge into the display.
  const holder = state?.holder ?? conflict?.holder;
  const holderName = holder?.displayName ?? t('common:holds.lock.anotherOperator');

  return (
    <div
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border border-amber-700/30 bg-amber-100 px-3 py-1 text-xs font-medium',
        'text-amber-900',
        'dark:bg-amber-900/40 dark:text-amber-100',
        className,
      )}
      role="status"
      aria-live="polite"
      data-state={isHeld ? 'held-by-other' : 'unknown'}
    >
      <ShieldAlert className="h-3 w-3" aria-hidden />
      <span>
        {t('common:holds.lock.lockedBy')} {holderName}
        {secondsRemaining > 0 ? ` · ${secondsRemaining}s` : ''}
      </span>
    </div>
  );
}
